import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../lib/api';
import { Spinner, ErrorState } from '../components/LoadingStates';
import { 
  BrainIcon, 
  ZapIcon, 
  PuzzleIcon, 
  BotIcon,
  DollarSignIcon,
  ClockIcon,
  AlertTriangleIcon,
  CheckCircleIcon,
  CalendarIcon,
  ActivityIcon
} from '../components/Icons';
import '../styles/usage-premium.css';

type TimePeriod = 'day' | 'week' | 'month';

interface ModelUsage {
  model: string;
  tokens: number;
  cost: number;
  percentOfTotal: number;
  color: string;
  gradient: string;
  icon: React.ReactNode;
  sparklineData: number[];
}

// Model configuration mapping with icons and colors
const modelConfig: Record<string, { name: string; icon: React.ReactNode; color: string; gradient: string }> = {
  'moonshot/kimi-k2.5': { 
    name: 'Kimi K2.5', 
    icon: <BrainIcon size={20} />, 
    color: '#8b5cf6',
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)'
  },
  'google/gemini-2.0-flash': { 
    name: 'Gemini Flash', 
    icon: <ZapIcon size={20} />, 
    color: '#3b82f6',
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)'
  },
  'google/gemini-2.5-pro': { 
    name: 'Gemini Pro', 
    icon: <PuzzleIcon size={20} />, 
    color: '#10b981',
    gradient: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)'
  },
  'ollama/minimax-m2.5:cloud': { 
    name: 'MiniMax', 
    icon: <BotIcon size={20} />, 
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)'
  },
};

// Circular Progress Gauge Component
const CircularGauge: React.FC<{ 
  percent: number; 
  color: string; 
  size?: number;
  strokeWidth?: number;
  children?: React.ReactNode;
}> = ({ percent, color, size = 140, strokeWidth = 10, children }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (Math.min(percent, 100) / 100) * circumference;
  
  // Determine status color
  const statusColor = percent > 95 ? '#ef4444' : percent > 80 ? '#f59e0b' : color;
  
  return (
    <div className="circular-progress" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle
          className="circular-progress-track"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          style={{ strokeWidth }}
        />
        <circle
          className="circular-progress-fill"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          style={{
            stroke: statusColor,
            strokeDasharray: circumference,
            strokeDashoffset,
            strokeWidth,
            filter: `drop-shadow(0 0 6px ${statusColor}40)`,
          }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {children}
      </div>
    </div>
  );
};

// Sparkline Chart Component
const Sparkline: React.FC<{ data: number[]; color: string; width?: number; height?: number }> = ({ 
  data, 
  color, 
  width = 60, 
  height = 24 
}) => {
  if (data.length < 2) return null;
  
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  
  const points = data.map((val, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((val - min) / range) * height;
    return `${x},${y}`;
  }).join(' ');
  
  const areaPoints = `0,${height} ${points} ${width},${height}`;
  
  return (
    <svg width={width} height={height} className="overflow-visible">
      <defs>
        <linearGradient id={`sparkline-gradient-${color.replace('#', '')}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon
        points={areaPoints}
        fill={`url(#sparkline-gradient-${color.replace('#', '')})`}
        className="sparkline-fill"
      />
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="sparkline"
      />
    </svg>
  );
};

// Tab Button Component
const TabButton: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`tab-btn ${active ? 'active' : ''}`}
  >
    {children}
  </button>
);

// Model Card Component
const ModelCard: React.FC<{ model: ModelUsage; totalCost: number; index: number }> = ({ 
  model, 
  totalCost,
  index 
}) => {
  const costPercent = totalCost > 0 ? (model.cost / totalCost) * 100 : 0;
  
  return (
    <div 
      className="model-card"
      style={{ 
        animationDelay: `${index * 50}ms`,
        animation: 'countUp 0.5s ease forwards',
        opacity: 0
      }}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div 
            className="model-icon"
            style={{ 
              background: `linear-gradient(135deg, ${model.color}20 0%, ${model.color}10 100%)`,
              color: model.color,
              boxShadow: `0 0 20px ${model.color}15`
            }}
          >
            {model.icon}
          </div>
          <div>
            <h4 className="text-slate-100 font-semibold text-sm">{model.model}</h4>
            <p className="text-slate-500 text-xs mt-0.5">{model.percentOfTotal.toFixed(0)}% of usage</p>
          </div>
        </div>
        <Sparkline data={model.sparklineData} color={model.color} />
      </div>
      
      <div className="space-y-3">
        <div className="flex items-baseline justify-between">
          <span className="text-slate-300 font-bold text-lg">${model.cost.toFixed(2)}</span>
          <span className="text-slate-500 text-xs">{model.tokens.toLocaleString()} tokens</span>
        </div>
        
        <div className="progress-bar-premium">
          <div 
            className="progress-bar-fill"
            style={{ 
              width: `${Math.min(model.percentOfTotal, 100)}%`,
              background: model.gradient
            }}
          />
        </div>
        
        <div className="flex justify-between items-center text-xs">
          <span className="text-slate-500">{costPercent.toFixed(0)}% of cost</span>
          <span 
            className="font-medium"
            style={{ color: model.color }}
          >
            {model.tokens > 1000000 ? `${(model.tokens / 1000000).toFixed(1)}M` : 
             model.tokens > 1000 ? `${(model.tokens / 1000).toFixed(1)}K` : model.tokens}
          </span>
        </div>
      </div>
    </div>
  );
};

// Main Component
export const UsageOverview: React.FC = () => {
  const [activePeriod, setActivePeriod] = useState<TimePeriod>('month');
  const [sessions, setSessions] = useState<any[]>([]);
  const [models, setModels] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadUsage = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getUsage();
      setSessions(data.sessions || []);
      setModels(data.models || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load usage data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsage();
    const interval = setInterval(loadUsage, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Calculate date boundaries
  const now = new Date();
  const today = now.toISOString().split('T')[0];
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - now.getDay());
  const weekStartStr = weekStart.toISOString().split('T')[0];

  // Process usage data based on period
  const usageData = useMemo(() => {
    const filteredSessions = sessions.filter((s: any) => {
      if (activePeriod === 'day') return s.date === today;
      if (activePeriod === 'week') return s.date >= weekStartStr;
      return true;
    });

    // Aggregate model data
    const sessionModelMap = new Map<string, { tokens: number; cost: number; history: number[] }>();
    
    filteredSessions.forEach((s: any) => {
      const existing = sessionModelMap.get(s.model) || { tokens: 0, cost: 0, history: [] };
      existing.tokens += (s.tokensIn || 0) + (s.tokensOut || 0);
      existing.cost += s.cost || 0;
      existing.history.push((s.tokensIn || 0) + (s.tokensOut || 0));
      sessionModelMap.set(s.model, existing);
    });

    let displayModels: ModelUsage[];
    let periodTokens: number;
    let totalCost: number;

    if (activePeriod === 'month') {
      const total = models.reduce((sum, m) => sum + (m.tokens || 0), 0);
      periodTokens = total;
      totalCost = models.reduce((sum, m) => sum + (m.cost || 0), 0);
      
      displayModels = models.map(m => {
        const config = modelConfig[m.model] || { 
          name: m.model, 
          icon: <BotIcon size={20} />, 
          color: '#6b7280',
          gradient: 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)'
        };
        const sessionData = sessionModelMap.get(m.model);
        return {
          model: config.name,
          tokens: m.tokens || 0,
          cost: m.cost || 0,
          percentOfTotal: total > 0 ? ((m.tokens || 0) / total) * 100 : 0,
          color: config.color,
          gradient: config.gradient,
          icon: config.icon,
          sparklineData: sessionData?.history.length ? sessionData.history : [m.tokens * 0.2, m.tokens * 0.4, m.tokens * 0.6, m.tokens * 0.8, m.tokens],
        };
      });
    } else {
      const allModelKeys = new Set([...Array.from(sessionModelMap.keys()), ...models.map((m: any) => m.model)]);
      
      periodTokens = Array.from(sessionModelMap.values()).reduce((sum, d) => sum + d.tokens, 0);
      totalCost = Array.from(sessionModelMap.values()).reduce((sum, d) => sum + d.cost, 0);
      
      displayModels = Array.from(allModelKeys).map(modelKey => {
        const sessionData = sessionModelMap.get(modelKey) || { tokens: 0, cost: 0, history: [] };
        const monthlyModel = models.find((m: any) => m.model === modelKey);
        const tokens = sessionData.tokens > 0 ? sessionData.tokens : (monthlyModel?.tokens || 0);
        const cost = sessionData.cost > 0 ? sessionData.cost : (monthlyModel?.cost || 0);
        
        const config = modelConfig[modelKey] || { 
          name: modelKey, 
          icon: <BotIcon size={20} />, 
          color: '#6b7280',
          gradient: 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)'
        };
        
        return {
          model: config.name,
          tokens,
          cost,
          percentOfTotal: periodTokens > 0 ? (tokens / periodTokens) * 100 : 0,
          color: config.color,
          gradient: config.gradient,
          icon: config.icon,
          sparklineData: sessionData.history.length ? sessionData.history : [tokens * 0.2, tokens * 0.4, tokens * 0.6, tokens * 0.8, tokens],
        };
      });
    }

    // Sort by cost descending
    displayModels.sort((a, b) => b.cost - a.cost);

    return {
      models: displayModels,
      totalTokens: periodTokens,
      totalCost,
    };
  }, [sessions, models, activePeriod, today, weekStartStr]);

  // Calculate limits
  const tokenLimit = activePeriod === 'month' ? 5000000 : activePeriod === 'week' ? 1500000 : 500000;
  const usagePercent = Math.min((usageData.totalTokens / tokenLimit) * 100, 100);
  
  // Status determination
  const status = usagePercent > 95 ? 'critical' : usagePercent > 80 ? 'warning' : 'healthy';
  const statusConfig = {
    healthy: { color: '#10b981', icon: <CheckCircleIcon size={16} />, text: 'On Track' },
    warning: { color: '#f59e0b', icon: <AlertTriangleIcon size={16} />, text: 'Approaching Limit' },
    critical: { color: '#ef4444', icon: <AlertTriangleIcon size={16} />, text: 'Limit Reached' },
  };

  if (loading) return <Spinner size="large" />;
  if (error) return <ErrorState message={error} onRetry={loadUsage} />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="section-header mb-0">
          <div className="section-icon">
            <ActivityIcon size={20} />
          </div>
          <div>
            <h2 className="section-title">Usage Overview</h2>
            <p className="section-subtitle">Monitor your LLM consumption</p>
          </div>
        </div>
        
        {/* Period Tabs */}
        <div className="flex gap-1 bg-slate-800/40 p-1 rounded-xl">
          <TabButton active={activePeriod === 'day'} onClick={() => setActivePeriod('day')}>
            Day
          </TabButton>
          <TabButton active={activePeriod === 'week'} onClick={() => setActivePeriod('week')}>
            Week
          </TabButton>
          <TabButton active={activePeriod === 'month'} onClick={() => setActivePeriod('month')}>
            Month
          </TabButton>
        </div>
      </div>

      {/* Main Stats Card with Circular Gauge */}
      <div className="gradient-border p-6 sm:p-8">
        <div className="flex flex-col lg:flex-row lg:items-center gap-8">
          {/* Left: Cost & Token Stats */}
          <div className="flex-1 space-y-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <DollarSignIcon size={14} className="text-slate-400" />
                <span className="stat-label">Total Cost ({activePeriod})</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="stat-value">${usageData.totalCost.toFixed(2)}</span>
                <span className="text-slate-500 text-lg">USD</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-800/50 rounded-lg">
                <CalendarIcon size={14} className="text-slate-400" />
                <span className="text-slate-300 text-sm font-medium">
                  {usageData.totalTokens.toLocaleString()} tokens
                </span>
              </div>
              
              <div 
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium"
                style={{ 
                  background: `${statusConfig[status].color}15`,
                  color: statusConfig[status].color
                }}
              >
                {statusConfig[status].icon}
                {statusConfig[status].text}
              </div>
            </div>
          </div>

          {/* Right: Circular Gauge */}
          <div className="flex items-center justify-center lg:justify-end">
            <CircularGauge 
              percent={usagePercent} 
              color="#6366f1"
              size={160}
              strokeWidth={12}
            >
              <span 
                className="text-3xl font-black"
                style={{ 
                  color: status === 'critical' ? '#ef4444' : status === 'warning' ? '#f59e0b' : '#f8fafc'
                }}
              >
                {usagePercent.toFixed(0)}%
              </span>
              <span className="text-slate-500 text-xs mt-1">
                {usageData.totalTokens.toLocaleString()} / {tokenLimit.toLocaleString()}
              </span>
            </CircularGauge>
          </div>
        </div>
      </div>

      {/* Model Breakdown */}
      <div>
        <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4">
          Usage by Model
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {usageData.models.length > 0 ? (
            usageData.models.map((model, index) => (
              <ModelCard 
                key={model.model} 
                model={model} 
                totalCost={usageData.totalCost}
                index={index}
              />
            ))
          ) : (
            <div className="col-span-full text-center py-12 text-slate-500 bg-slate-800/20 rounded-2xl border border-slate-700/30">
              <BotIcon size={32} className="mx-auto mb-3 opacity-50" />
              <p>No usage data for this period</p>
            </div>
          )}
        </div>
      </div>

      {/* Last Updated Footer */}
      <div className="flex items-center justify-center gap-2 text-slate-600 text-xs pt-2">
        <ClockIcon size={12} />
        <span>Last updated: {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
      </div>
    </div>
  );
};
